import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../api/client";
import { useAuth } from "../context/AuthContext";
import ChipInput from "../components/ChipInput";

const NAVY  = "#1B3A5C";
const TEAL  = "#0D7377";
const AMBER = "#F0A500";

// Tennis leagues use REGEXP prefix matching in the cost engine (ATP%, WTA%, ITF%)
// so these shorthand values are valid contract league entries even though they
// don't appear as exact competition values in provider_event_mapping
const KNOWN_CONTRACT_LEAGUES = ["All", "ATP", "WTA", "ITF", "Challenger"];

// ── Allowed values ────────────────────────────────────────────────────────────
const ALLOWED = {
  cost_type:          ["Data","Data & Streaming","Streaming","Tax","Levy","Match Tracker","Virtual Games","Web Content","Platform Fee"],
  cost_metric_type:   ["Per Bet","Cumulative","Per Event","Per Stream"],
  payment_structure:  ["Monthly","Quarterly","Annual"],
  payment_currency:   ["GBP","EUR","USD","AUD","CAD","SEK","NOK","DKK","ZAR","BRL","INR","JPY","HKD"],
  freebet_cost_type:  ["Excluded","Included","Not Applicable","Undefined"],
  inplay_cost_type:   ["All Bets","InPlay Only","Stream"],
  is_minimum_guarantee: ["Yes","No"],
  cost_selection_split: ["Not Applicable", "Weighted", "Relative", "Undefined"],
  cost_deduction_type:  ["Not Applicable","Percentage","Fixed Value"],
  cumulative_structure: ["Not Applicable","Annual","Fiscal Year","Quarterly","Monthly"],
};

const COST_METRICS = {
  "Per Bet":    ["GGR","FreeBetGGR","PostTaxGGR","PostTaxFreeBetGGR","Turnover","Fixed Fee Per Payment Structure"],
  "Cumulative": ["GGR","FreeBetGGR","PostTaxGGR","PostTaxFreeBetGGR","Turnover"],
  "Per Event":  ["Fixed Fee Per Event", "Fixed Fee Per Payment Structure"],
  "Per Stream": ["Fixed Fee Per Stream"],
};

const EMPTY_FORM = {
  contract_row_id: "",
  contract_name: "", provider: "", provider_sub_tier: "Not Applicable",
  cost_type: "", start_date: "", end_date: "",
  payment_structure: "", payment_currency: "", domain_brand: "",
  sport: "", league: "", event_country: "",
  freebet_cost_type: "", inplay_cost_type: "",
  cost_metric_type: "", cumulative_range: "Not Applicable",
  cumulative_structure: "Not Applicable", cost_metric: "",
  cost_metric_amount: "", cost_selection_split: "Not Applicable",
  quoted_events: "Not Applicable", cost_metric_calculation: "",
  cost_deduction_type: "Not Applicable", cost_deduction_amount: "Not Applicable",
  is_minimum_guarantee: "No", minimum_guarantee: "Not Applicable",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function Badge({ children, color = "gray" }) {
  const colors = {
    gray:  { backgroundColor: "#f3f4f6", color: "#374151" },
    teal:  { backgroundColor: "#E6F4F1", color: "#0D7377" },
    amber: { backgroundColor: "#FEF3C7", color: "#92400E" },
    red:   { backgroundColor: "#FEF2F2", color: "#991B1B" },
    green: { backgroundColor: "#F0FDF4", color: "#166534" },
  };
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
      style={colors[color] || colors.gray}>
      {children}
    </span>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="w-8 h-8 border-4 border-gray-200 rounded-full animate-spin"
        style={{ borderTopColor: TEAL }} />
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
      {message}
    </div>
  );
}

const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white";

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function SelectField({ value, onChange, options, placeholder, disabled }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      className={inputClass + (disabled ? " opacity-50 cursor-not-allowed" : "")}>
      <option value="">{placeholder || "Select..."}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// ── Contract form ─────────────────────────────────────────────────────────────
function ContractForm({
  initial, isDuplicate, brands, providers,
  allSports, allLeagues, allCountries,
  onQueue, onCancel, isAdmin, isSuperAdmin,
}) {
  const [form, setForm] = useState(
  isDuplicate ? { ...initial, contract_row_id: "" } : (initial || EMPTY_FORM));
  const [error,           setError]           = useState(null);
  const [isOverride,      setIsOverride]      = useState(false);
  const [overrideReason,  setOverrideReason]  = useState("");
  const [filteredLeagues, setFilteredLeagues] = useState(allLeagues);
  const [subTierOptions,  setSubTierOptions]  = useState(["Not Applicable"]);

  const isEdit = !!initial && !isDuplicate;

  // Filter leagues when sport chips change
  useEffect(() => {
    const selectedSports = form.sport
      ? form.sport.split(",").map(s => s.trim()).filter(Boolean)
      : [];

    if (selectedSports.length === 0 || selectedSports.includes("All")) {
      // allLeagues already includes KNOWN_CONTRACT_LEAGUES merged in from parent
      setFilteredLeagues(allLeagues);
      return;
    }

    apiClient.get("/api/ref/leagues", { params: { sports: selectedSports.join(",") } })
      .then(res => setFilteredLeagues(
        [...new Set([...KNOWN_CONTRACT_LEAGUES, ...res.data])].sort()
      ))
      .catch(() => setFilteredLeagues(allLeagues));
  }, [form.sport, allLeagues]);

  // Fetch sub tier options when provider changes
  useEffect(() => {
    if (!form.provider) {
      setSubTierOptions(["Not Applicable"]);
      return;
    }
    apiClient.get("/api/ref/subtiers", { params: { provider: form.provider } })
      .then(res => {
        setSubTierOptions(res.data);
        if (!res.data.includes(form.provider_sub_tier)) {
          setForm(f => ({ ...f, provider_sub_tier: "Not Applicable" }));
        }
      })
      .catch(() => setSubTierOptions(["Not Applicable"]));
  }, [form.provider]);

  function set(field, value) {
    setForm(f => {
      const updated = { ...f, [field]: value };
      if (field === "cost_metric_type") updated.cost_metric = "";
      if (field === "cost_deduction_type" && value === "Not Applicable") {
        updated.cost_deduction_amount = "Not Applicable";
      }
      if (field === "is_minimum_guarantee" && value === "No") {
        updated.minimum_guarantee = "Not Applicable";
      }
      if (field === "cost_metric_type" && value !== "Cumulative") {
        updated.cumulative_range     = "Not Applicable";
        updated.cumulative_structure = "Not Applicable";
      }
      return updated;
    });
  }

  function handleQueue() {
    setError(null);
    const required = ["contract_name","provider","cost_type","start_date","end_date",
                      "payment_structure","payment_currency","domain_brand","sport",
                      "cost_metric_type","cost_metric"];
    const missing = required.filter(f => !form[f]);
    if (missing.length) {
      setError(`Required fields missing: ${missing.join(", ")}`);
      return;
    }
    if (form.end_date <= form.start_date) {
      setError("End date must be after start date.");
      return;
    }
    if (isEdit && isSuperAdmin && isOverride && !overrideReason.trim()) {
      setError("Override reason is required for superadmin overrides.");
      return;
    }
    const payload = {
      ...form,
      cost_metric_amount: form.cost_metric_amount !== "" ? Number(form.cost_metric_amount) : null,
    };
    const type = isEdit
      ? (isSuperAdmin && isOverride ? "override" : "edit")
      : "create";
    onQueue({
      type,
      payload,
      overrideReason: isOverride ? overrideReason : undefined,
      label: `${type === "create" ? "Add" : type === "override" ? "Override" : "Edit"}: ${form.contract_name || "unnamed"}`,
    });
  }

  const costMetrics  = COST_METRICS[form.cost_metric_type] || [];
  const isCumulative = form.cost_metric_type === "Cumulative";
  const hasDeduction = form.cost_deduction_type && form.cost_deduction_type !== "Not Applicable";
  const hasMg        = form.is_minimum_guarantee === "Yes";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-bold" style={{ color: NAVY }}>
            {isDuplicate ? "Duplicate Row" : isEdit ? "Edit Row" : "Add Contract Row"}
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {isEdit ? "Changes queued until you commit" : "Queued until you commit to database"}
          </p>
        </div>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
      </div>

      {/* Save to queue button — top */}
      <div className="px-5 pt-4">
        {error && <ErrorBox message={error} />}
        <button onClick={handleQueue}
          className="w-full py-2 rounded-lg text-white text-sm font-medium mb-4"
          style={{ backgroundColor: TEAL }}>
          {isDuplicate ? "Queue as New Row" : isEdit ? "Queue Changes" : "Queue New Row"}
        </button>
      </div>

      {/* Form body */}
      <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-4">

        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Contract Identity</p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Contract Name" required>
            <input type="text" value={form.contract_name}
              onChange={e => set("contract_name", e.target.value)}
              className={inputClass} placeholder="e.g. BetGenius Data & Streaming" />
          </Field>
          <Field label="Cost Type" required>
            <SelectField value={form.cost_type} onChange={v => set("cost_type", v)}
              options={ALLOWED.cost_type} />
          </Field>
         </div> 

        <div className="grid grid-cols-2 gap-3">
          <Field label="Provider" required>
            <input
              list="provider-options"
              value={form.provider || ''}
              onChange={e => set("provider", e.target.value)}
              placeholder="Select or type provider..."
              className={inputClass}
            />
            <datalist id="provider-options">
              {providers.map((p, i) => (
                <option key={i} value={p} />
              ))}
            </datalist>
          </Field>
          <Field label="Provider Sub Tier">
            <SelectField
              value={form.provider_sub_tier}
              onChange={v => set("provider_sub_tier", v)}
              options={subTierOptions}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Start Date" required>
            <input type="date" value={form.start_date}
              onChange={e => set("start_date", e.target.value)} className={inputClass} />
          </Field>
          <Field label="End Date" required>
            <input type="date" value={form.end_date}
              onChange={e => set("end_date", e.target.value)} className={inputClass} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Payment Structure" required>
            <SelectField value={form.payment_structure}
              onChange={v => set("payment_structure", v)}
              options={ALLOWED.payment_structure} />
          </Field>
          <Field label="Payment Currency" required>
            <SelectField value={form.payment_currency}
              onChange={v => set("payment_currency", v)}
              options={ALLOWED.payment_currency} />
          </Field>
        </div>

        <div className="relative">
          <Field label="Domain / Brand" required>
            <ChipInput
              value={form.domain_brand}
              onChange={v => set("domain_brand", v)}
              options={brands}
              placeholder="Type brand or 'All'..."
            />
          </Field>
        </div>

        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-2">Coverage</p>

        <div className="relative">
          <Field label="Sport" required>
            <ChipInput value={form.sport} onChange={v => set("sport", v)}
              options={allSports}
              placeholder="Type sport or 'All', comma for multiple..." />
          </Field>
          <p className="text-xs text-gray-400 mt-1">Use All for all sports, or e.g. Football, Cricket</p>
        </div>

        <div className="relative">
          <Field label="League">
            <ChipInput value={form.league} onChange={v => set("league", v)}
              options={filteredLeagues}
              placeholder="Type league or 'All'..." />
          </Field>
          {form.sport && !form.sport.includes("All") && (
            <p className="text-xs text-gray-400 mt-1">Filtered to selected sport(s)</p>
          )}
        </div>

        <div className="relative">
          <Field label="Event Country">
            <ChipInput value={form.event_country} onChange={v => set("event_country", v)}
              options={allCountries}
              placeholder="Type country or 'All', comma for multiple..." />
          </Field>
        </div>

        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-2">Cost Structure</p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Freebet Cost Type">
            <SelectField value={form.freebet_cost_type}
              onChange={v => set("freebet_cost_type", v)}
              options={ALLOWED.freebet_cost_type} />
          </Field>
          <Field label="InPlay Cost Type">
            <SelectField value={form.inplay_cost_type}
              onChange={v => set("inplay_cost_type", v)}
              options={ALLOWED.inplay_cost_type} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cost Metric Type" required>
            <SelectField value={form.cost_metric_type}
              onChange={v => set("cost_metric_type", v)}
              options={ALLOWED.cost_metric_type} />
          </Field>
          <Field label="Cost Metric" required>
            <SelectField value={form.cost_metric} onChange={v => set("cost_metric", v)}
              options={costMetrics}
              placeholder={form.cost_metric_type ? "Select metric" : "Select type first"}
              disabled={!form.cost_metric_type} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cost Metric Amount">
            <input type="number" step="any" value={form.cost_metric_amount}
              onChange={e => set("cost_metric_amount", e.target.value)}
              className={inputClass} placeholder="e.g. 0.05" />
          </Field>
          <Field label="Cost Selection Split">
            <SelectField value={form.cost_selection_split}
              onChange={v => set("cost_selection_split", v)}
              options={ALLOWED.cost_selection_split} />
          </Field>
        </div>

        {isCumulative && (
          <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50/40 rounded-lg border border-blue-100">
            <Field label="Cumulative Range">
              <input type="text" value={form.cumulative_range}
                onChange={e => set("cumulative_range", e.target.value)}
                className={inputClass}
                placeholder="e.g. 0-1000000 or >=1000001" />
              <p className="text-xs text-gray-400 mt-1">Each tier is a separate row — use Duplicate</p>
            </Field>
            <Field label="Cumulative Structure">
              <SelectField value={form.cumulative_structure}
                onChange={v => set("cumulative_structure", v)}
                options={ALLOWED.cumulative_structure}
                disabled={!form.cumulative_range || form.cumulative_range === "Not Applicable"} />
            </Field>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cost Metric Calculation">
            <input type="text" value={form.cost_metric_calculation}
              onChange={e => set("cost_metric_calculation", e.target.value)}
              className={inputClass} />
          </Field>
          <Field label="Quoted Events">
            <input type="text" value={form.quoted_events}
              onChange={e => set("quoted_events", e.target.value)}
              className={inputClass} placeholder="Not Applicable" />
          </Field>
        </div>

        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-2">Cost Deduction</p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cost Deduction Type">
            <SelectField value={form.cost_deduction_type}
              onChange={v => set("cost_deduction_type", v)}
              options={ALLOWED.cost_deduction_type} />
          </Field>
          {hasDeduction && (
            <Field label={form.cost_deduction_type === "Percentage" ? "Deduction Amount (%)" : "Deduction Amount"}>
              <input type="number" step="any"
                value={form.cost_deduction_amount === "Not Applicable" ? "" : form.cost_deduction_amount}
                onChange={e => set("cost_deduction_amount", e.target.value)}
                className={inputClass}
                placeholder={form.cost_deduction_type === "Percentage" ? "e.g. 0.05 for 5%" : "e.g. 1000"} />
            </Field>
          )}
        </div>

        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-2">Minimum Guarantee</p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Is Minimum Guarantee">
            <SelectField value={form.is_minimum_guarantee}
              onChange={v => set("is_minimum_guarantee", v)}
              options={ALLOWED.is_minimum_guarantee} />
          </Field>
          {hasMg && (
            <Field label="Minimum Guarantee Amount">
              <input type="text"
                value={form.minimum_guarantee === "Not Applicable" ? "" : form.minimum_guarantee}
                onChange={e => set("minimum_guarantee", e.target.value)}
                className={inputClass} placeholder="e.g. 100000" />
            </Field>
          )}
        </div>

        {isSuperAdmin && isEdit && (
          <div className="border border-amber-200 rounded-xl p-4 bg-amber-50 mt-2">
            <div className="flex items-center gap-2 mb-2">
              <input type="checkbox" id="override" checked={isOverride}
                onChange={e => setIsOverride(e.target.checked)}
                style={{ accentColor: AMBER }} />
              <label htmlFor="override" className="text-sm font-medium" style={{ color: "#92400E" }}>
                Superadmin Override — requires written reason
              </label>
            </div>
            {isOverride && (
              <textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                className={inputClass + " mt-2"} rows={3}
                placeholder="Explain why this override is necessary..." />
            )}
          </div>
        )}

        <button onClick={handleQueue}
          className="w-full py-2 rounded-lg text-white text-sm font-medium mt-2"
          style={{ backgroundColor: TEAL }}>
          {isDuplicate ? "Queue as New Row" : isEdit ? "Queue Changes" : "Queue New Row"}
        </button>
      </div>
    </div>
  );
}

// ── History panel ─────────────────────────────────────────────────────────────
function HistoryPanel({ contractName, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get(`/api/contracts/${encodeURIComponent(contractName)}/history`)
      .then(res => { setHistory(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [contractName]);

  const changeColors = { CREATE: "green", EDIT: "teal", OVERRIDE: "amber", DELETE: "red" };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-6 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-bold" style={{ color: NAVY }}>Change History</h2>
          <p className="text-xs text-gray-400 mt-0.5">{contractName}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? <LoadingSpinner /> : history.length === 0 ? (
          <p className="text-gray-400 text-sm">No change history found.</p>
        ) : (
          <div className="space-y-3">
            {history.map((row, i) => {
              const ts   = row.changed_at?.value || row.changed_at;
              const date = ts ? new Date(ts).toLocaleString("en-GB") : "—";
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge color={changeColors[row.change_type] || "gray"}>{row.change_type}</Badge>
                      <span className="text-xs font-medium text-gray-700">{row.field_name}</span>
                    </div>
                    <span className="text-xs text-gray-400">{date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {row.previous_value && (
                      <>
                        <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded line-through">{row.previous_value}</span>
                        <span className="text-gray-400">→</span>
                      </>
                    )}
                    <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded">{row.new_value || "—"}</span>
                  </div>
                  {row.override_reason && (
                    <p className="text-xs text-amber-700 mt-2 italic">Override reason: {row.override_reason}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{row.changed_by_email}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Delete confirmation ───────────────────────────────────────────────────────
function DeleteConfirm({ row, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-bold mb-2" style={{ color: NAVY }}>Delete Contract Row</h3>
        <p className="text-sm text-gray-600 mb-1">Are you sure? This cannot be undone.</p>
        <div className="bg-gray-50 rounded-lg p-3 my-4 text-xs text-gray-600 space-y-1">
          <p><strong>Contract:</strong> {row.contract_name}</p>
          <p><strong>Sport:</strong> {row.sport}</p>
          <p><strong>Cost Type:</strong> {row.cost_type}</p>
          <p><strong>Cost Metric:</strong> {row.cost_metric}</p>
          <p><strong>Period:</strong> {(row.start_date?.value || row.start_date)?.slice(0,10)} → {(row.end_date?.value || row.end_date)?.slice(0,10)}</p>
        </div>
        <p className="text-xs text-gray-400 mb-4">The deletion will be logged in contract history.</p>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          <button onClick={onConfirm}
            className="px-4 py-2 text-sm rounded-lg text-white font-medium"
            style={{ backgroundColor: "#C62828" }}>
            Delete Row
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Contracts page ───────────────────────────────────────────────────────
export default function Contracts() {
  const { userInfo } = useAuth();
  const isAdmin      = userInfo?.role === "superadmin" || userInfo?.role === "client_admin";
  const isSuperAdmin = userInfo?.role === "superadmin";

  const [contracts,    setContracts]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [expanded,     setExpanded]     = useState(new Set());
  const [brands,       setBrands]       = useState([]);
  const [providers,    setProviders]    = useState([]);
  const [allSports,    setAllSports]    = useState([]);
  const [allLeagues,   setAllLeagues]   = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [panel,        setPanel]        = useState(null);
  const [search,       setSearch]       = useState("");
  const [queue,        setQueue]        = useState([]);
  const [committing,   setCommitting]   = useState(false);
  const [commitError,  setCommitError]  = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Fetch reference data once on mount
  useEffect(() => {
    apiClient.get("/api/ref/brands")   .then(r => setBrands(r.data.filter(b => b !== "All"))).catch(() => {});
    apiClient.get("/api/ref/providers").then(r => setProviders(r.data)).catch(() => {});
    apiClient.get("/api/ref/sports")   .then(r => setAllSports(r.data)).catch(() => {});
    apiClient.get("/api/ref/leagues")  .then(r => setAllLeagues(
      [...new Set([...KNOWN_CONTRACT_LEAGUES, ...r.data])].sort()
    )).catch(() => setAllLeagues(KNOWN_CONTRACT_LEAGUES));
    apiClient.get("/api/ref/countries").then(r => setAllCountries(r.data)).catch(() => {});
  }, []);

  const fetchContracts = useCallback(() => {
    setLoading(true);
    apiClient.get("/api/contracts/")
      .then(res => { setContracts(res.data); setError(null); })
      .catch(() => setError("Failed to load contracts."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  function handleQueue(operation) {
    setQueue(q => [...q, operation]);
    setPanel(null);
  }

  async function handleCommit() {
    if (!queue.length) return;
    setCommitting(true);
    setCommitError(null);
    let failed = [];

    for (const op of queue) {
      try {
        if (op.type === "create") {
          await apiClient.post("/api/contracts/", op.payload);
        } else if (op.type === "edit") {
          await apiClient.put("/api/contracts/row", op.payload);
        } else if (op.type === "override") {
          await apiClient.put("/api/contracts/row/override", {
            ...op.payload, override_reason: op.overrideReason,
          });
        } else if (op.type === "delete") {
          await apiClient.delete("/api/contracts/row", { data: op.payload });
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        failed.push(`${op.label}: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
      }
    }

    setCommitting(false);
    if (failed.length) setCommitError(`${failed.length} operation(s) failed:\n${failed.join("\n")}`);
    setQueue([]);
    fetchContracts();
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const row = deleteTarget;
    setQueue(q => [...q, {
      type: "delete",
      label: `Delete: ${row.contract_name} (${row.sport} / ${row.cost_type})`,
      payload: {
        contract_row_id:   row.contract_row_id,
        contract_name:     row.contract_name,
        start_date:        row.start_date?.value || row.start_date,
        end_date:          row.end_date?.value   || row.end_date,
        sport:             row.sport,
        domain_brand:      row.domain_brand,
        cost_type:         row.cost_type,
        cost_metric:       row.cost_metric,
        cumulative_range:  row.cumulative_range || null,
        provider_sub_tier: row.provider_sub_tier || null,
      }
    }]);
    setDeleteTarget(null);
  }

  const grouped = {};
  contracts
    .filter(r => !search || r.contract_name.toLowerCase().includes(search.toLowerCase()))
    .forEach(row => {
      if (!grouped[row.contract_name]) grouped[row.contract_name] = [];
      grouped[row.contract_name].push(row);
    });
  const groupedList = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));

  function toggleExpand(name) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }

  function rowToForm(row) {
    return {
      contract_row_id:         row.contract_row_id         || "",
      contract_name:           row.contract_name           || "",
      provider:                row.provider                || "",
      provider_sub_tier:       row.provider_sub_tier       || "Not Applicable",
      cost_type:               row.cost_type               || "",
      start_date:              row.start_date?.value       || row.start_date || "",
      end_date:                row.end_date?.value         || row.end_date   || "",
      payment_structure:       row.payment_structure       || "",
      payment_currency:        row.payment_currency        || "",
      domain_brand:            row.domain_brand            || "",
      sport:                   row.sport                   || "",
      league:                  row.league                  || "",
      event_country:           row.event_country           || "",
      freebet_cost_type:       row.freebet_cost_type       || "",
      inplay_cost_type:        row.inplay_cost_type        || "",
      cost_metric_type:        row.cost_metric_type        || "",
      cumulative_range:        row.cumulative_range        || "Not Applicable",
      cumulative_structure:    row.cumulative_structure    || "Not Applicable",
      cost_metric:             row.cost_metric             || "",
      cost_metric_amount:      row.cost_metric_amount != null ? String(row.cost_metric_amount) : "",
      cost_selection_split:    row.cost_selection_split    || "Not Applicable",
      quoted_events:           row.quoted_events           || "Not Applicable",
      cost_metric_calculation: row.cost_metric_calculation || "",
      cost_deduction_type:     row.cost_deduction_type     || "Not Applicable",
      cost_deduction_amount:   row.cost_deduction_amount   || "Not Applicable",
      is_minimum_guarantee:    row.is_minimum_guarantee    || "No",
      minimum_guarantee:       row.minimum_guarantee       || "Not Applicable",
    };
  }

  function exportCSV() {
    if (!contracts.length) return;
    const headers = Object.keys(contracts[0]).join(",");
    const rows = contracts.map(r =>
      Object.values(r).map(v => `"${String(v?.value || v || "").replace(/"/g,'""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([headers + "\n" + rows], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "contracts.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex gap-6">
      {deleteTarget && (
        <DeleteConfirm
          row={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className={`flex-1 min-w-0 ${panel ? "max-w-3xl" : ""}`}>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: NAVY }}>Contracts</h1>
            <p className="text-sm text-gray-500 mt-1">
              {contracts.length} rows across {groupedList.length} contracts
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCSV}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
              Export CSV
            </button>
            {isAdmin && (
              <button onClick={() => setPanel({ type: "add" })}
                className="px-4 py-2 text-sm rounded-lg text-white font-medium"
                style={{ backgroundColor: TEAL }}>
                + Add Contract Row
              </button>
            )}
          </div>
        </div>

        {queue.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: "#92400E" }}>
                  {queue.length} unsaved change{queue.length !== 1 ? "s" : ""} queued
                </p>
                <div className="mt-1 space-y-0.5">
                  {queue.map((op, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <p className="text-xs text-amber-700">{op.label}</p>
                      <button
                        onClick={() => setQueue(q => q.filter((_, qi) => qi !== i))}
                        className="text-xs text-amber-400 hover:text-amber-700">✕</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setQueue([])}
                  className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">
                  Clear all
                </button>
                <button onClick={handleCommit} disabled={committing}
                  className="px-4 py-1.5 text-sm rounded-lg text-white font-medium"
                  style={{ backgroundColor: committing ? "#94a3b8" : NAVY }}>
                  {committing ? "Saving..." : "Commit to Database"}
                </button>
              </div>
            </div>
            {commitError && <ErrorBox message={commitError} />}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by contract name..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>

        {loading ? <LoadingSpinner /> : error ? <ErrorBox message={error} /> :
          contracts.length === 0 && queue.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-16 text-center">
              <p className="text-gray-400 text-lg mb-2">No contracts yet</p>
              {isAdmin && (
                <>
                  <p className="text-gray-300 text-sm mb-6">Add your first contract to get started</p>
                  <button onClick={() => setPanel({ type: "add" })}
                    className="px-6 py-2.5 text-sm rounded-lg text-white font-medium"
                    style={{ backgroundColor: TEAL }}>
                    + Add First Contract
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="w-8 px-4 py-3" />
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Contract</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Provider</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Period</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rows</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cost Types</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {groupedList.map(([name, rows]) => {
                    const isOpen    = expanded.has(name);
                    const firstRow  = rows[0];
                    const costTypes = [...new Set(rows.map(r => r.cost_type))];
                    // Earliest start / latest end across all rows in the group (covers renewals)
                    const startDates = rows.map(r => r.start_date?.value || r.start_date).filter(Boolean);
                    const endDates   = rows.map(r => r.end_date?.value   || r.end_date).filter(Boolean);
                    const startDate  = startDates.sort()[0];
                    const endDate    = endDates.sort().slice(-1)[0];

                    return (
                      <React.Fragment key={name}>
                        <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleExpand(name)}>
                          <td className="px-4 py-3 text-gray-400 text-xs">{isOpen ? "▼" : "▶"}</td>
                          <td className="px-4 py-3 font-semibold" style={{ color: NAVY }}>{name}</td>
                          <td className="px-4 py-3 text-gray-600">{firstRow.provider}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {startDate?.slice(0,10)} → {endDate?.slice(0,10)}
                          </td>
                          <td className="px-4 py-3">
                            <Badge color="teal">{rows.length} row{rows.length !== 1 ? "s" : ""}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 flex-wrap">
                              {costTypes.map(ct => <Badge key={ct}>{ct}</Badge>)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={e => { e.stopPropagation(); setPanel({ type: "history", contractName: name }); }}
                              className="text-xs text-gray-400 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100">
                              History
                            </button>
                          </td>
                        </tr>

                        {isOpen && rows.map((row, ri) => {
                          const sd = (row.start_date?.value || row.start_date)?.slice(0,10);
                          const ed = (row.end_date?.value   || row.end_date)?.slice(0,10);
                          return (
                            <tr key={ri} className="border-b border-gray-50 bg-blue-50/30 hover:bg-blue-50/50">
                              <td className="px-4 py-2.5 pl-8" />
                              <td className="px-4 py-2.5 text-xs text-gray-500">
                                {row.sport || "—"} / {row.domain_brand || "—"}
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-500">{row.cost_type}</td>
                              <td className="px-4 py-2.5 text-xs text-gray-500">{sd} → {ed}</td>
                              <td className="px-4 py-2.5 text-xs text-gray-500">{row.cost_metric_type}</td>
                              <td className="px-4 py-2.5 text-xs text-gray-500">
                                {row.cost_metric} {row.cost_metric_amount != null ? `@ ${row.cost_metric_amount}` : ""}
                              </td>
                              <td className="px-4 py-2.5">
                                {isAdmin && (
                                  <div className="flex gap-1">
                                    <button onClick={() => setPanel({ type: "edit", row: rowToForm(row) })}
                                      className="text-xs px-2 py-1 rounded text-teal-600 hover:bg-teal-50">
                                      Edit
                                    </button>
                                    <button onClick={() => setPanel({ type: "duplicate", row: rowToForm(row) })}
                                      className="text-xs px-2 py-1 rounded text-gray-500 hover:bg-gray-100">
                                      Duplicate
                                    </button>
                                    <button onClick={() => setDeleteTarget(row)}
                                      className="text-xs px-2 py-1 rounded text-red-400 hover:bg-red-50">
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {panel && (
        <div className="w-[500px] flex-shrink-0 bg-white rounded-xl shadow-lg border border-gray-100 flex flex-col"
          style={{ height: "calc(100vh - 120px)", position: "sticky", top: "80px" }}>
          {panel.type === "history" ? (
            <HistoryPanel contractName={panel.contractName} onClose={() => setPanel(null)} />
          ) : (
            <ContractForm
              initial={panel.row || null}
              isDuplicate={panel.type === "duplicate"}
              brands={brands}
              providers={providers}
              allSports={allSports}
              allLeagues={allLeagues}
              allCountries={allCountries}
              isAdmin={isAdmin}
              isSuperAdmin={isSuperAdmin}
              onQueue={handleQueue}
              onCancel={() => setPanel(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}